import ThemedModal from "#/components/shared/ThemedModal";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import Textarea from "#/components/shared/textarea";
import { useToast } from "#/hooks/ToastContext";
import { getArticleAssetReviewContext, reviseArticle } from "#/services/articles";
import type { JSX } from "react";
import { useMemo, useState } from "react";
import useArticleAssetReview from "#/admin/articles/hooks/useArticleAssetReview";
import {
  renderBlock,
  renderBlockPlacements,
  renderStandalonePlacements,
} from "./blockRenderers";
import { createPlacementIndex } from "./placementIndex";
import {
  createSectionIndex,
  groupBlocksBySection,
  sortSections,
} from "./sectionHelpers";
import type { ArticlePayload } from "./types";
import ArticleHeaderWithReviews from "./components/ArticleHeaderWithReviews";
import BlockReviewRow from "./components/BlockReviewRow";
import ReviewEditModal from "./components/ReviewEditModal";
import SectionHeaderWithReviews from "./components/SectionHeaderWithReviews";
import type { ReviewCard, ReviewCards } from "./components/reviewTypes";

export function BlockRenderer({
  payload,
  reviewCards,
  onAssetReviewSubmitted,
}: {
  payload: ArticlePayload;
  reviewCards?: ReviewCards;
  onAssetReviewSubmitted?: () => void;
}) {
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [suggestionTarget, setSuggestionTarget] = useState<{
    label: string;
    text: string;
    type: string;
    id?: string;
    key?: string | null;
  } | null>(null);
  const [suggestionText, setSuggestionText] = useState("");
  const [suggestionInstructions, setSuggestionInstructions] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assetReviewOpen, setAssetReviewOpen] = useState(false);
  const [assetReviewAssetId, setAssetReviewAssetId] = useState<string | null>(null);
  const [assetReviewBasePrompt, setAssetReviewBasePrompt] = useState("");
  const [assetReviewNote, setAssetReviewNote] = useState("");
  const { showToast } = useToast();
  const assetReviewMutation = useArticleAssetReview();

  const { article, blocks, assets, sections } = payload;

  const cards = useMemo(
    () => ({
      article: reviewCards?.article ?? [],
      sections: reviewCards?.sections ?? {},
      blocks: reviewCards?.blocks ?? {},
    }),
    [reviewCards]
  );

  const [draftNotes, setDraftNotes] = useState<Record<string, string>>({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalCard, setEditModalCard] = useState<ReviewCard | null>(null);

  const placementIndex = createPlacementIndex(assets);
  const sectionIndex = createSectionIndex(sections);
  const groupedBlocks = groupBlocksBySection(blocks);
  const sortedSections = sortSections(sections);

  const consumedPlacementIds = new Set<string>();
  const handleSuggest = (payload: {
    label: string;
    text: string;
    type: string;
    id?: string;
    key?: string | null;
  }) => {
    setSuggestionTarget(payload);
    setSuggestionText("");
    setSuggestionInstructions("");
    setSuggestionOpen(true);
  };

  const context = {
    placements: placementIndex,
    allPlacements: assets ?? [],
    consumedPlacementIds,
    onSuggest: handleSuggest,
    onReviewAsset: async ({ assetId, basePrompt }: { assetId: string; basePrompt?: string }) => {
      console.log("[AdminImageReview] open modal for asset", { assetId, basePromptPreview: basePrompt?.slice(0, 80) });
      setAssetReviewAssetId(assetId);
      setAssetReviewBasePrompt(basePrompt ?? "");
      setAssetReviewNote("");
      setAssetReviewOpen(true);

      try {
        const context = await getArticleAssetReviewContext(assetId);
        console.log("[AdminImageReview] fetched asset review context", context);
        if (typeof context.basePrompt === "string") {
          setAssetReviewBasePrompt(context.basePrompt);
        }
      } catch (error) {
        console.error("[AdminImageReview] failed to fetch asset review context", {
          assetId,
          error,
        });
      }
    },
  };

  const articleLevelFigures = renderStandalonePlacements(
    placementIndex.globalPlacements,
    context
  );

  const groupBySectionId = new Map(
    groupedBlocks.map((group) => [group.sectionId ?? "root", group])
  );

  const orderedGroups = [
    ...sortedSections.map((section) => ({
      sectionId: section.id,
      blocks: groupBySectionId.get(section.id)?.blocks ?? [],
    })),
    ...(groupBySectionId.get("root") ? [groupBySectionId.get("root")!] : []),
  ].filter((group) => group.blocks.length > 0);

  const rendered = orderedGroups
    .map((group, groupIndex) => {
      const sectionKeys = new Set<string>();
      if (group.sectionId) {
        sectionKeys.add(group.sectionId);
      }

      const section = group.sectionId
        ? sectionIndex.get(group.sectionId)
        : undefined;

      const elements: JSX.Element[] = [];
      const normalizedSectionTitle = section?.title
        ? section.title.trim().toLowerCase()
        : null;
      let skippedDuplicateHeading = false;

      if (section?.id) {
        sectionKeys.add(section.id);
      }
      if (section?.key) {
        sectionKeys.add(section.key);
      }
      if (section?.slug) {
        sectionKeys.add(section.slug);
      }

      for (const key of Array.from(sectionKeys)) {
        const placementsForKey = placementIndex.bySectionId.get(key);
        if (placementsForKey?.length) {
          const figures = renderStandalonePlacements(placementsForKey, context);
          if (figures.length) {
            elements.push(...figures);
          }
        }
      }

      for (let index = 0; index < group.blocks.length; index += 1) {
        const currentBlock = group.blocks[index];
        if (
          section?.title &&
          currentBlock?.type?.toLowerCase() === "heading" &&
          !skippedDuplicateHeading
        ) {
          const spanText =
            currentBlock.text_spans
              ?.map((span) => span.text)
              .filter((value): value is string => Boolean(value))
              .join("") ?? "";
          const resolvedText =
            spanText || currentBlock.text || currentBlock.content || "";
          if (
            normalizedSectionTitle &&
            resolvedText.trim().toLowerCase() === normalizedSectionTitle
          ) {
            skippedDuplicateHeading = true;

            const skippedBlockPlacements = renderBlockPlacements(
              currentBlock.id,
              context
            );
            if (skippedBlockPlacements.length) {
              elements.push(...skippedBlockPlacements);
            }

            continue;
          }
        }
        const { elements: blockElements, skip } = renderBlock({
          blocks: group.blocks,
          index,
          context,
        });

        const blockReviewCards = currentBlock?.id
          ? cards.blocks[currentBlock.id]
          : undefined;
        if (blockReviewCards?.length && currentBlock?.id) {
          elements.push(
            <BlockReviewRow
              blockId={currentBlock.id}
              content={<>{blockElements}</>}
              cards={blockReviewCards}
              onEdit={(card) => {
                setDraftNotes((prev) => ({
                  ...prev,
                  [card.id]: card.notes ?? "",
                }));
                setEditModalCard(card);
                setEditModalOpen(true);
              }}
              onDelete={(card) => card.onDelete?.(card.id)}
            />
          );
        } else {
          elements.push(...blockElements);
        }
        index += skip;
      }

      if (elements.length === 0) {
        return null;
      }

      if (!group.sectionId) {
        return (
          <div key={`group-${groupIndex}`} className="flex flex-col gap-10">
            {elements}
          </div>
        );
      }

      if (!section) {
        return (
          <div
            key={`group-${group.sectionId}`}
            className="flex flex-col gap-10"
          >
            {elements}
          </div>
        );
      }

      const anchor = section.key ?? section.slug ?? section.id;
      const headingId = `${anchor}-heading`;

      const sectionReviewCards = group.sectionId
        ? cards.sections[group.sectionId]
        : undefined;

      return (
        <section
          key={group.sectionId}
          id={anchor}
          className="space-y-6"
          aria-labelledby={section.title ? headingId : undefined}
        >
          {section.title ? (
            <SectionHeaderWithReviews
              title={section.title}
              headingId={headingId}
              onSuggest={() =>
                handleSuggest({
                  label: "Section",
                  text: section.title ?? "",
                  type: "section",
                  id: section.id,
                  key: section.key ?? null,
                })
              }
              cards={sectionReviewCards}
              onEdit={(card) => {
                setDraftNotes((prev) => ({
                  ...prev,
                  [card.id]: card.notes ?? "",
                }));
                setEditModalCard(card);
                setEditModalOpen(true);
              }}
              onDelete={(card) => card.onDelete?.(card.id)}
            />
          ) : null}
          <div className="flex flex-col gap-10">{elements}</div>
        </section>
      );
    })
    .filter(Boolean) as JSX.Element[];

  return (
    <>
      <article className="mx-auto w-full max-w-5xl rounded-2xl bg-white px-6 py-12 text-black md:px-10 lg:px-0">
        <header className="mb-10 space-y-6">
          <ArticleHeaderWithReviews
            title={article.title}
            figures={articleLevelFigures.length ? (
              <div className="flex flex-col gap-6">{articleLevelFigures}</div>
            ) : undefined}
            cards={cards.article}
            onEdit={(card) => {
              setDraftNotes((prev) => ({
                ...prev,
                [card.id]: card.notes ?? "",
              }));
              setEditModalCard(card);
              setEditModalOpen(true);
            }}
            onDelete={(card) => card.onDelete?.(card.id)}
          />
        </header>

        <div className="flex flex-col gap-10">{rendered}</div>
      </article>
      <ThemedModal
        isOpen={suggestionOpen}
        onClose={() => setSuggestionOpen(false)}
        title="Suggest a change"
        description={
          suggestionTarget
            ? `${suggestionTarget.label}: ${suggestionTarget.text}`
            : undefined
        }
        tone="neutral"
        footer={
          <>
            <ActionButton
              tone="secondary"
              onClick={() => setSuggestionOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </ActionButton>
            <ActionButton
              isLoading={isSubmitting}
              onClick={async () => {
                if (!suggestionText.trim()) {
                  showToast("Please enter a revision.", "error");
                  return;
                }
                if (!suggestionInstructions.trim()) {
                  showToast("Please add instructions.", "error");
                  return;
                }
                if (!suggestionTarget) return;
                setIsSubmitting(true);
                try {
                  await reviseArticle({
                    articleId: article.id,
                    revisions: suggestionText.trim(),
                    instructions: suggestionInstructions.trim(),
                    article: {
                      id: article.id,
                      slug: article.slug,
                      title: article.title,
                      version: article.version,
                      heroAssetId: article.heroAssetId ?? undefined,
                    },
                    sections: sections ?? undefined,
                    blocks: blocks ?? undefined,
                    assets: assets ?? undefined,
                    targets: [
                      {
                        type: suggestionTarget.type,
                        id: suggestionTarget.id,
                        key: suggestionTarget.key ?? undefined,
                        suggestion: suggestionText.trim(),
                      },
                    ],
                  });
                  showToast("Revision request sent.", "success");
                  setSuggestionOpen(false);
                } catch (error) {
                  const message =
                    error instanceof Error
                      ? error.message
                      : "Failed to send revision request.";
                  showToast(message, "error");
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              Save suggestion
            </ActionButton>
          </>
        }
      >
        <Textarea
          id="article-suggestion-text"
          label="Revision request"
          placeholder="Describe the revision you'd like to see."
          value={suggestionText}
          onChange={(event) => setSuggestionText(event.target.value)}
          rows={5}
          fullWidth
        />
        <div className="mt-4">
          <Textarea
            id="article-suggestion-instructions"
            label="Instructions"
            placeholder="Keep tone clinical, avoid simplifications."
            value={suggestionInstructions}
            onChange={(event) => setSuggestionInstructions(event.target.value)}
            rows={3}
            fullWidth
          />
        </div>
      </ThemedModal>
      <ThemedModal
        isOpen={assetReviewOpen}
        onClose={() => {
          setAssetReviewOpen(false);
          setAssetReviewAssetId(null);
          setAssetReviewBasePrompt("");
          setAssetReviewNote("");
        }}
        title="Review image with AI"
        description="Edit the base image prompt and add an optional note before regenerating."
        tone="neutral"
        footer={
          <>
            <ActionButton
              tone="secondary"
              onClick={() => {
                setAssetReviewOpen(false);
                setAssetReviewAssetId(null);
                setAssetReviewBasePrompt("");
                setAssetReviewNote("");
              }}
              disabled={assetReviewMutation.isPending}
            >
              Cancel
            </ActionButton>
            <ActionButton
              isLoading={assetReviewMutation.isPending}
              onClick={async () => {
                if (!assetReviewAssetId) return;
                try {
                  await assetReviewMutation.mutateAsync({
                    assetId: assetReviewAssetId,
                    basePrompt: assetReviewBasePrompt.trim() || undefined,
                    note: assetReviewNote.trim() || undefined,
                  });
                  setAssetReviewOpen(false);
                  setAssetReviewAssetId(null);
                  setAssetReviewBasePrompt("");
                  setAssetReviewNote("");
                  onAssetReviewSubmitted?.();
                } catch {
                  // toast is handled by mutation onError
                }
              }}
            >
              Submit review
            </ActionButton>
          </>
        }
      >
        <Textarea
          id="asset-review-base-prompt"
          label="Base prompt"
          placeholder="Describe the image to generate..."
          value={assetReviewBasePrompt}
          onChange={(event) => setAssetReviewBasePrompt(event.target.value)}
          rows={5}
          fullWidth
        />
        {!assetReviewBasePrompt.trim() ? (
          <p className="mt-2 text-xs text-brown-600">
            No saved base prompt was found for this asset. Add one here to set it.
          </p>
        ) : null}
        <div className="mt-4">
          <Textarea
            id="asset-review-note"
            label="Optional note"
            placeholder="Example: verify this image matches the section’s clinical details and remove text overlays."
            value={assetReviewNote}
            onChange={(event) => setAssetReviewNote(event.target.value)}
            rows={4}
            fullWidth
          />
        </div>
      </ThemedModal>
      <ReviewEditModal
        isOpen={editModalOpen}
        card={editModalCard}
        draft={
          editModalCard
            ? draftNotes[editModalCard.id] ?? editModalCard.notes ?? ""
            : ""
        }
        onChange={(value) => {
          if (!editModalCard) return;
          setDraftNotes((prev) => ({
            ...prev,
            [editModalCard.id]: value,
          }));
        }}
        onCancel={() => {
          setEditModalOpen(false);
          setEditModalCard(null);
        }}
        onSave={async () => {
          if (!editModalCard?.onUpdateNotes) return;
          const draft = draftNotes[editModalCard.id] ?? "";
          await editModalCard.onUpdateNotes(editModalCard.id, draft);
          setEditModalOpen(false);
          setEditModalCard(null);
        }}
      />
    </>
  );
}

export default BlockRenderer;
